import { motion } from "framer-motion";

/**
 * ArmillarySphere - A 3D rotating golden astrolabe/armillary sphere
 * Used in the Birth Time onboarding step for mystical atmosphere
 */
export const ArmillarySphere = ({ className = "" }) => {
    return (
        <div className={`relative w-48 h-48 sm:w-56 sm:h-56 ${className}`}>
            {/* Outer glow */}
            <motion.div
                className="absolute inset-0 rounded-full"
                style={{
                    background: "radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 70%)",
                    filter: "blur(20px)"
                }}
                animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.5, 0.8, 0.5]
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
            
            {/* Main sphere container */}
            <div className="absolute inset-0 flex items-center justify-center">
                {/* Rotating rings - Outer */}
                <motion.div
                    className="absolute w-full h-full"
                    animate={{ rotateY: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    style={{ transformStyle: "preserve-3d", perspective: "1000px" }}
                >
                    <svg viewBox="0 0 200 200" className="w-full h-full">
                        <defs>
                            <linearGradient id="goldGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.9" />
                                <stop offset="50%" stopColor="#FFD700" stopOpacity="1" />
                                <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.9" />
                            </linearGradient>
                        </defs>
                        {/* Outer ring */}
                        <ellipse
                            cx="100" cy="100" rx="90" ry="90"
                            fill="none"
                            stroke="url(#goldGrad1)"
                            strokeWidth="2"
                            opacity="0.8"
                        />
                        {/* Degree markers */}
                        {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((deg) => (
                            <line
                                key={deg}
                                x1={100 + 85 * Math.cos((deg * Math.PI) / 180)}
                                y1={100 + 85 * Math.sin((deg * Math.PI) / 180)}
                                x2={100 + 90 * Math.cos((deg * Math.PI) / 180)}
                                y2={100 + 90 * Math.sin((deg * Math.PI) / 180)}
                                stroke="#D4AF37"
                                strokeWidth="1.5"
                                opacity="0.6"
                            />
                        ))}
                    </svg>
                </motion.div>
                
                {/* Middle tilted ring */}
                <motion.div
                    className="absolute w-[85%] h-[85%]"
                    animate={{ rotateX: 360, rotateZ: 15 }}
                    transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                    style={{ transformStyle: "preserve-3d" }}
                >
                    <svg viewBox="0 0 200 200" className="w-full h-full">
                        <ellipse
                            cx="100" cy="100" rx="85" ry="40"
                            fill="none"
                            stroke="#D4AF37"
                            strokeWidth="1.5"
                            opacity="0.7"
                        />
                    </svg>
                </motion.div>
                
                {/* Inner tilted ring (opposite direction) */}
                <motion.div
                    className="absolute w-[70%] h-[70%]"
                    animate={{ rotateY: -360, rotateX: -25 }}
                    transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                    style={{ transformStyle: "preserve-3d" }}
                >
                    <svg viewBox="0 0 200 200" className="w-full h-full">
                        <ellipse
                            cx="100" cy="100" rx="80" ry="35"
                            fill="none"
                            stroke="#D4AF37"
                            strokeWidth="1.5"
                            opacity="0.6"
                        />
                    </svg>
                </motion.div>
                
                {/* Innermost vertical ring */}
                <motion.div
                    className="absolute w-[55%] h-[55%]"
                    animate={{ rotateZ: 360 }}
                    transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                >
                    <svg viewBox="0 0 200 200" className="w-full h-full">
                        <ellipse
                            cx="100" cy="100" rx="70" ry="70"
                            fill="none"
                            stroke="#D4AF37"
                            strokeWidth="1"
                            opacity="0.5"
                            strokeDasharray="5 10"
                        />
                    </svg>
                </motion.div>
                
                {/* Central Earth sphere */}
                <motion.div
                    className="absolute w-8 h-8 sm:w-10 sm:h-10 rounded-full"
                    style={{
                        background: "radial-gradient(circle at 30% 30%, #D4AF37 0%, #8B7355 50%, #4A3728 100%)",
                        boxShadow: "0 0 20px rgba(212,175,55,0.5), inset 0 0 10px rgba(0,0,0,0.3)"
                    }}
                    animate={{
                        boxShadow: [
                            "0 0 20px rgba(212,175,55,0.5), inset 0 0 10px rgba(0,0,0,0.3)",
                            "0 0 30px rgba(212,175,55,0.7), inset 0 0 10px rgba(0,0,0,0.3)",
                            "0 0 20px rgba(212,175,55,0.5), inset 0 0 10px rgba(0,0,0,0.3)"
                        ]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                />
                
                {/* Zodiac symbols floating around */}
                {['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'].map((symbol, i) => (
                    <motion.span
                        key={i}
                        className="absolute text-primary/40 text-xs font-serif"
                        style={{
                            left: `${50 + 42 * Math.cos((i * 30 * Math.PI) / 180)}%`,
                            top: `${50 + 42 * Math.sin((i * 30 * Math.PI) / 180)}%`,
                            transform: "translate(-50%, -50%)"
                        }}
                        animate={{
                            opacity: [0.3, 0.6, 0.3],
                            scale: [1, 1.1, 1]
                        }}
                        transition={{
                            duration: 3,
                            repeat: Infinity,
                            delay: i * 0.25
                        }}
                    >
                        {symbol}
                    </motion.span>
                ))}
            </div>
        </div>
    );
};

export default ArmillarySphere;
